import type { GasPayload } from '@atlaskit/analytics-gas-types';

// oxlint-disable typescript-eslint(consistent-type-imports)
import { CONFIG_TASK_SUFFIXES } from './config-task-suffixes';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { CONFIG_TASK_SUFFIXES } from './config-task-suffixes';

/**
 * Attributes shape for analytics events.
 */
export type AnalyticsAttributes = {
	attributes: {
		commonAttributes?: Record<string, unknown>;
		additionalAttributes?: Record<string, unknown>;
	};
	nonPrivacySafeAttributes?: Record<string, unknown>;
};

/**
 * Analytics event payload type.
 * Combines GAS payload with our attribute structure.
 */
export type FireAnalyticsEvent = GasPayload & AnalyticsAttributes;

/**
 * Degradable dependency types for config fetching.
 */
export type DegradableDependency =
	| 'search-config'
	| 'search-3p-config'
	| 'outbound-auth-github'
	| 'search-combined-config';

/**
 * Cache request names for tracking cache access.
 */
export type CacheRequestName =
	| 'search-config'
	| 'search-3p-config'
	| 'outbound-auth-github'
	| 'search-combined-config';

export type ConfigTaskSuffix = (typeof CONFIG_TASK_SUFFIXES)[keyof typeof CONFIG_TASK_SUFFIXES];

/**
 * Builds a full task name from the surface prefix and task suffix.
 * @param surfacePrefix - e.g., 'search-page', 'quick-find', 'dynamic-cards'
 * @param suffix - one of CONFIG_TASK_SUFFIXES values
 */
export const buildTaskName = (surfacePrefix: string, suffix: ConfigTaskSuffix): string =>
	`${surfacePrefix}/${suffix}`;

/**
 * Required analytics configuration that consumers must provide.
 * This ensures all search surfaces fire consistent analytics.
 */
export interface ConfigFetchAnalytics {
	/**
	 * The search session ID for correlating events.
	 * Typically obtained from useSearchSessionId() in FPS.
	 */
	searchSessionId: string;

	/**
	 * The source of the analytics event (e.g., 'fullPageSearch', 'quickFind').
	 * Used in the event payload to identify where the event came from.
	 */
	source: string;

	/**
	 * The action subject for analytics events (e.g., 'searchPage', 'quickFind').
	 * This identifies the surface type in analytics.
	 */
	actionSubject: string;

	/**
	 * Prefix for task names in vital stats (e.g., 'search-page', 'quick-find').
	 * Full task name becomes: `${taskNamePrefix}/first-party-config-ux`
	 */
	taskNamePrefix: string;

	/**
	 * Function to fire analytics events.
	 * Typically the fireAnalyticsEvent from useAnalytics() in FPS.
	 */
	fireAnalyticsEvent: (event: FireAnalyticsEvent) => void;

	/**
	 * Function to log exceptions.
	 * Typically from useLogException() in FPS.
	 */
	logException: (error: Error) => void;
}
